import { hashTypedData, recoverAddress } from 'viem'

import { assertValidSignatureInput } from '../common/validation'

import type {
  EIP712SigningPayload,
  SignatureInput,
  ValidationResult,
} from '../../types'

/**
 * Validates an EIP-712 signed transaction against an expected payload.
 */
export async function validateEIP712Signature(
  payload: EIP712SigningPayload,
  signature: SignatureInput,
): Promise<ValidationResult> {
  try {
    assertValidSignatureInput(signature.signature)
    if (!/^0x[0-9a-fA-F]{130}$/.test(signature.signature)) {
      return {
        success: false,
        message: 'Invalid EVM signature format',
        details: 'Signature must be 132 characters starting with 0x (65 bytes)',
      }
    }

    const payloadHash = hashTypedData(payload.payload)
    const recoveredAddress = await recoverAddress({
      hash: payloadHash,
      signature: signature.signature as `0x${string}`,
    })

    if (
      recoveredAddress.toLowerCase() !== payload.walletAddress.toLowerCase()
    ) {
      return {
        success: false,
        message: 'Address mismatch - signature verification failed',
        details: `Recovered: ${recoveredAddress}, Expected: ${payload.walletAddress}`,
      }
    }

    return {
      success: true,
      message: 'EVM signature verified successfully',
      details: `Address ${recoveredAddress} successfully recovered from EIP-712 signature`,
    }
  } catch (error) {
    return {
      success: false,
      message: 'Signature verification failed',
      details:
        error instanceof Error ? error.message : 'Unknown cryptographic error',
    }
  }
}
